import { useTranslate } from "@@/translations/use-translate";
import { css } from "@emotion/react";
import type { BackgroundStyle, Translatable } from "@towni/common";
import { useRef } from "react";
import type { FlexAlignment } from "../flex-containers";
import { useBackgroundStyleToCss } from "../stylable/background-style-css";

type Props = React.JSX.IntrinsicElements["div"] & {
    readonly direction?: "row" | "column";
    readonly mainAxis?: FlexAlignment;
    readonly crossAxis?: FlexAlignment;
    readonly background?: BackgroundStyle | null;
    readonly title?: Translatable;
    readonly fillParent?: boolean;
    readonly fillParentHeight?: boolean;
    readonly fillParentWidth?: boolean;
};

/**
 * FlexBox div component that provides a flexible container with customizable
 * main axis and cross axis alignment, background styling, and optional title.
 *
 * NOTE! css changes of this component does NOT
 * trigger a re-render a.t.m.
 *
 * @param {Props} props - The properties passed to the FlexBox component.
 * @param {string} [props.title] - Optional title for the FlexBox, which will be translated.
 * @param {string} [props.direction="row"] - The direction of the flex container, defaults to "row".
 * @param {string} [props.mainAxis] - The alignment of items along the main axis.
 * @param {string} [props.crossAxis] - The alignment of items along the cross axis.
 * @param {object} [props.background] - Optional background styling for the FlexBox.
 * @param {React.ReactNode} props.children - The child elements to be rendered inside the FlexBox.
 *
 * @returns {React.JSX.Element} The rendered FlexBox component.
 */
const FlexBox = (props: Props): React.JSX.Element => {
    const translate = useTranslate();
    const title = props.title ? translate(props.title) : undefined;
    const { mainAxis, crossAxis, ...rest } = props;
    const backgroundStyleToCss = useBackgroundStyleToCss();
    const backgroundCss = props.background
        ? backgroundStyleToCss(props.background)
        : undefined;
    const cssRef = useRef(
        css({
            display: "flex",
            flexDirection: props.direction ?? "row",
            justifyContent: mainAxis,
            alignItems: crossAxis,
            width:
                props.fillParent || props.fillParentWidth ? "100%" : undefined,
            height:
                props.fillParent || props.fillParentHeight ? "100%" : undefined,
            ...backgroundCss,
        }),
    );

    return (
        <div {...rest} css={cssRef.current} title={title}>
            {props.children}
        </div>
    );
};

/**
 * A functional component that renders a FlexBox div with a row direction. \
 * mainAxis is set to "flex-start" and crossAxis is set to "center" by default.
 *
 * @param {Omit<Props, "direction">} props - The properties passed to the FlexBox component, excluding the "direction" property.
 * @returns {React.JSX.Element} A FlexBox div component with the direction set to "row".
 */
const FlexRow2 = (props: Omit<Props, "direction">): React.JSX.Element => (
    <FlexBox
        mainAxis="flex-start"
        crossAxis="center"
        {...props}
        direction="row"
    />
);

/**
 * A functional component that renders a FlexBox div with a column direction. \
 * mainAxis is set to "flex-start" and crossAxis is set to "stretch" by default.
 *
 * @param {Omit<Props, "direction">} props - The properties passed to the FlexBox component, excluding the "direction" property.
 * @returns {React.JSX.Element} A FlexBox div component with the direction set to "row".
 */
const FlexColumn2 = (props: Omit<Props, "direction">): React.JSX.Element => (
    <FlexBox
        mainAxis="flex-start"
        crossAxis="stretch"
        {...props}
        direction="column"
    />
);

export { FlexBox, FlexColumn2, FlexRow2 };
